import type { PropsWithChildren, ReactNode } from 'react'
import type { TooltipProps as AriaTooltipProps } from 'react-aria-components'
import {
  Tooltip as AriaTooltip,
  OverlayArrow,
  TooltipTrigger,
  composeRenderProps,
} from 'react-aria-components'
import { tv } from 'tailwind-variants'

export type RACTooltipProps = PropsWithChildren<
  Omit<AriaTooltipProps, 'children'>
>

export const tooltipStyles = tv({
  base: 'group bg-modal-background text-sm rounded-lg drop-shadow-lg will-change-transform shadow-[0_0_20px_0_#0000001a]',
  variants: {
    isEntering: {
      true: 'animate-in fade-in placement-bottom:slide-in-from-top-0.5 placement-top:slide-in-from-bottom-0.5 placement-left:slide-in-from-right-0.5 placement-right:slide-in-from-left-0.5 ease-out duration-200',
    },
    isExiting: {
      true: 'animate-out fade-out placement-bottom:slide-out-to-top-0.5 placement-top:slide-out-to-bottom-0.5 placement-left:slide-out-to-right-0.5 placement-right:slide-out-to-left-0.5 ease-in duration-150',
    },
  },
})

export function UITooltip(props: {
  children?: RACTooltipProps['children']
  TooltipProps?: Omit<RACTooltipProps, 'children'>
}) {
  const { children, TooltipProps } = props
  return (
    <AriaTooltip
      offset={10}
      {...TooltipProps}
      className={composeRenderProps(
        TooltipProps?.className,
        (className, renderProps) =>
          tooltipStyles({ ...renderProps, className }),
      )}
    >
      <UITooltipArrow />
      {children}
    </AriaTooltip>
  )
}

export function UITooltipArrow() {
  return (
    <OverlayArrow>
      <svg
        width={12}
        height={12}
        viewBox="0 0 12 12"
        className="fill-modal-background group-placement-bottom:rotate-180 group-placement-left:-rotate-90 group-placement-right:rotate-90"
      >
        <path d="M0 0 L4 4 L8 0" />
      </svg>
    </OverlayArrow>
  )
}

export type UITooltipTriggerWrapperProps = {
  trigger: ReactNode
  delay?: number

  TooltipProps?: Omit<
    RACTooltipProps,
    'children' | 'placement' | 'style' | 'className'
  >
} & Pick<RACTooltipProps, 'children' | 'placement'> &
  StyleProps

/**
 * Note: Currently, the trigger has to be a RAC `<Button />`
 * Otherwise the Tooltip won't display at all
 *
 * @param param0
 * @returns
 */
export function UITooltipTriggerWrapper({
  trigger,
  delay,
  children,
  TooltipProps,
  ...rest
}: UITooltipTriggerWrapperProps) {
  return (
    <TooltipTrigger delay={delay}>
      {trigger}
      <UITooltip
        {...{ children, TooltipProps: { ...TooltipProps, ...rest } }}
      />
    </TooltipTrigger>
  )
}
